import {
  ArrowLeftRight,
  Banknote,
  CalendarDays,
  ClipboardCheck,
  FileEdit,
  ListChecks,
  MapPinOff,
  Package,
  Receipt,
  ScrollText,
  Smile,
  Timer,
  Trophy,
  type LucideIcon,
} from 'lucide-react';
import type { PendingActionSource } from '../../api/pendingActions';

// Presentation metadata per source. `label` is a flat key in the `requests`
// i18n namespace (keySeparator is false — literal strings).

interface SourceMeta {
  icon: LucideIcon;
  label: string;
}

const FALLBACK: SourceMeta = { icon: ClipboardCheck, label: 'Request' };

const SOURCE_META: Record<PendingActionSource, SourceMeta> = {
  Leave: { icon: CalendarDays, label: 'Leave' },
  ShiftSwap: { icon: ArrowLeftRight, label: 'Shift swap' },
  Claim: { icon: Receipt, label: 'Claim' },
  AttendanceCorrection: { icon: FileEdit, label: 'Correction' },
  ShortLeave: { icon: Timer, label: 'Short leave' },
  Encashment: { icon: Banknote, label: 'Encashment' },
  AssetRequest: { icon: Package, label: 'Asset' },
  ShiftSwapAcceptance: { icon: ArrowLeftRight, label: 'Swap for you' },
  Onboarding: { icon: ListChecks, label: 'Onboarding' },
  Terms: { icon: ScrollText, label: 'Terms' },
  Survey: { icon: Smile, label: 'Pulse survey' },
  ReturnToWork: { icon: ClipboardCheck, label: 'Return to work' },
  Referee: { icon: Trophy, label: 'Referee' },
  OutOfBoundsClock: { icon: MapPinOff, label: 'Out of bounds' },
};

export function sourceMeta(source: string): SourceMeta {
  return SOURCE_META[source as PendingActionSource] ?? FALLBACK;
}

type TFunc = (key: string, opts?: Record<string, unknown>) => string;

/** True when two dates fall on the same calendar day (local time). */
function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Locale-formatted compact date span ("19–20 Jul" / "19 Jul" / "29 Jul – 2 Aug"),
 * mirroring the server's three cases but rendered per the caller's locale. Uses
 * `Intl.DateTimeFormat.formatRange` when available so the month is never repeated
 * needlessly and the dash/order follow the locale.
 */
function formatDateRange(startIso: string | undefined, endIso: string | undefined, locale: string): string | undefined {
  if (!startIso) return undefined;
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return undefined;
  const dtf = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  const end = endIso ? new Date(endIso) : null;
  if (!end || Number.isNaN(end.getTime()) || isSameDay(start, end)) {
    return dtf.format(start);
  }
  const withRange = dtf as Intl.DateTimeFormat & { formatRange?: (a: Date, b: Date) => string };
  if (typeof withRange.formatRange === 'function') {
    try {
      return withRange.formatRange(start, end);
    } catch {
      /* fall through to the manual join */
    }
  }
  return `${dtf.format(start)} – ${dtf.format(end)}`;
}

/**
 * The system-generated part of a Leave item's subtitle ("2 days · 19–20 Jul"),
 * localized on the client from the item's structured Meta (numberOfDays /
 * startDate / endDate). The leave TYPE is user data and stays server-provided
 * (the row's title). Returns undefined when the Meta is missing so the caller
 * can fall back to the server's English subtitle.
 */
export function leaveSubtitle(meta: Record<string, string> | undefined, locale: string, t: TFunc): string | undefined {
  if (!meta) return undefined;
  const range = formatDateRange(meta.startDate, meta.endDate, locale);
  if (!range) return undefined;
  const days = Number(meta.numberOfDays);
  const daysLabel =
    Number.isFinite(days) && days > 0
      ? days === 1
        ? t('1 day')
        : t('{{n}} days', { n: days.toLocaleString(locale) })
      : undefined;
  return daysLabel ? `${daysLabel} · ${range}` : range;
}

/**
 * Compact relative timestamp ("2m ago" / "3h ago" / "5d ago", older falls back
 * to a locale date). Mirrors the mobile inbox so both clients read the same.
 */
export function relativeTime(iso: string | undefined, locale: string, t: TFunc): string {
  if (!iso) return '';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '';
  const diffMs = Date.now() - when.getTime();
  const minutes = Math.floor(diffMs / 60000);
  // {{n}} (not {{count}}) on purpose: `count` triggers i18next plural-suffix
  // lookups these flat keys don't provide.
  if (minutes < 1) return t('Just now');
  if (minutes < 60) return t('{{n}}m ago', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('{{n}}h ago', { n: hours });
  const days = Math.floor(hours / 24);
  if (days <= 7) return t('{{n}}d ago', { n: days });
  return when.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}
